/**
 * Calendar write commands.
 *
 * TWO tools, deliberately, for what an agent will hear as one request. Moving
 * an article "to Friday" is either a planner display change or a publication
 * to the client's live WordPress site, and those are different systems with
 * different blast radii. A single `planner_schedule` is how "move it to
 * Friday" publishes a client's article, so the split is the safety property
 * and the names say which is which.
 *
 * Neither command mints an id. Both consume one from `planner_find_articles`
 * (Table's command, sharing this file's date arguments through
 * `plannerDates.ts`), and both accept a title instead, returning the candidate
 * list inside a refusal when it is ambiguous rather than guessing.
 */
import { z } from 'zod';
import {
  defineCommand,
  type CommitAuthorization,
  type Diff,
  type PlannerArticleSummary,
  type Receipt,
  type Refusal,
  type ResolveInput,
  type TenantId,
  type WriteDeps,
} from '../contract';
import { isRefusal, parseRequiredIsoDate, toCalendarDate } from '../plannerDates';
import { dispatchAndWait } from '../handshake';

/**
 * The event `planner_set_calendar_date` dispatches once its write has landed,
 * and which ContentCalendarView answers with `tool-completion-${requestId}`
 * after the grid has re-rendered from refreshed data.
 *
 * Exported so the page listens for the same string the tool sends. A literal
 * repeated in two files is how a handshake silently stops handshaking: nothing
 * fails, the tool just always times out and every receipt carries a false
 * "did not confirm" caveat.
 */
export const PLANNER_SET_CALENDAR_DATE_EVENT = 'webmcp:planner-set-calendar-date';

/* -------------------------------------------------------------------------- */
/* Shared selection                                                            */
/* -------------------------------------------------------------------------- */

/** Hoisted to module scope. big-calendar re-registered on every store tick by
 *  passing inline schema literals; these are defined once. */
const SELECTOR = {
  articleId: z.string().uuid().optional().describe('Exact article id from planner_find_articles.'),
  title: z.string().min(3).optional().describe('Exact or partial article title, used when no id is known.'),
};

const setCalendarDateArgs = z.object({
  ...SELECTOR,
  date: z.string().describe('Calendar date as YYYY-MM-DD, for example "2026-09-18".'),
});

const scheduleWpPublishArgs = z.object({
  ...SELECTOR,
  publishAt: z
    .string()
    .describe('When it goes live on WordPress, as a full ISO 8601 instant, for example "2026-09-18T14:00:00Z".'),
});

interface CalendarTarget {
  readonly article: PlannerArticleSummary;
}
interface SetDateTarget extends CalendarTarget {
  readonly date: string;
}
interface WpPublishTarget extends CalendarTarget {
  readonly publishAt: Date;
}

function refuse(because: string, extra: Partial<Refusal> = {}): Refusal {
  return { kind: 'refused', because, ...extra };
}

/**
 * Turns a loose selector into exactly one article, or refuses with candidates.
 *
 * The read seam is optional on `ReadDeps` by design, so a capability that does
 * not supply it makes these commands refuse rather than silently act on a
 * guess. That is the wrong-capability case, which is a refusal and not a bug.
 */
async function selectArticle(
  input: ResolveInput<{ articleId?: string; title?: string }>,
): Promise<PlannerArticleSummary | Refusal> {
  const { args, read, tenant } = input;

  if (!args.articleId && !args.title) {
    return refuse('Name the article by articleId or title.', {
      remedy: { command: 'planner_find_articles', why: 'lists the articles and their ids for a date range' },
    });
  }
  if (!read.findArticles) {
    return refuse('This surface cannot look up articles, so it cannot safely identify the one you mean.');
  }

  const found = await read.findArticles(tenant, {
    titleContains: args.title,
    limit: 25,
  });

  const matches = args.articleId
    ? found.filter((a) => a.id === args.articleId)
    : found;

  if (matches.length === 0) {
    return refuse(
      args.articleId
        ? `No article with id ${args.articleId} is in this workspace's planner.`
        : `No article matches "${args.title}".`,
      {
        remedy: { command: 'planner_find_articles', why: 'shows what is actually on the calendar' },
        retryable: true,
      },
    );
  }

  if (matches.length > 1) {
    // Candidates inside the refusal, so the agent picks rather than guesses.
    return refuse(`"${args.title}" matches ${matches.length} articles. Re-run with articleId.`, {
      candidates: matches.slice(0, 10).map((a) => ({ id: a.id, label: `${a.title} (${a.status})` })),
    });
  }

  return matches[0];
}

function articleDiff(
  target: CalendarTarget,
  tenant: TenantId,
  field: string,
  from: string | null,
  to: string,
): Diff {
  return {
    table: 'blog_content',
    rowId: target.article.id,
    tenant,
    changes: [{ field, from, to, authorability: 'exact' }],
    // Neither command edits prose that projects into a generation prompt.
    reachesGeneratedContent: false,
  };
}

/* -------------------------------------------------------------------------- */
/* planner_set_calendar_date                                                   */
/* -------------------------------------------------------------------------- */

export const plannerSetCalendarDate = defineCommand<
  z.infer<typeof setCalendarDateArgs>,
  SetDateTarget
>({
  name: 'planner_set_calendar_date',
  section: 'calendar',
  consequence: 'writes-config',
  description:
    'Move an article to a different date on the content planner calendar. This changes the planner only and never touches the client WordPress site. To publish on a date, use planner_schedule_wp_publish instead.',
  args: setCalendarDateArgs,

  async resolve(input) {
    const article = await selectArticle(input);
    if (isRefusal(article)) return article;

    const parsed = parseRequiredIsoDate(input.args.date, 'date');
    if (isRefusal(parsed)) return parsed;

    return { article, date: toCalendarDate(parsed) };
  },

  diff(target, tenant) {
    return articleDiff(target, tenant, 'scheduled_date', target.article.calendarDate, target.date);
  },

  async run(target: SetDateTarget, auth: CommitAuthorization, write: WriteDeps): Promise<Receipt | Refusal> {
    if (!write.setCalendarDate) {
      return refuse('This surface cannot change planner dates.');
    }

    const receipt = await write.setCalendarDate(auth, target.article.id, target.date);
    if (isRefusal(receipt)) return receipt;

    // The write landed; the chip has not moved yet. Wait for the calendar to
    // say it re-rendered from refreshed data, so the receipt cannot claim a
    // move the operator (or the witness) cannot see. `confirmed: false` is a
    // caveat on a write that DID happen, never a failure.
    const settled = await dispatchAndWait(PLANNER_SET_CALENDAR_DATE_EVENT, {
      contentId: target.article.id,
      date: target.date,
    });

    if (settled.confirmed) return receipt;

    return {
      ...receipt,
      summary: `${receipt.summary} The calendar did not confirm the chip moved within ${Math.round(settled.waitedMs / 1000)}s, so reload if it still shows the old date.`,
    };
  },
});

/* -------------------------------------------------------------------------- */
/* planner_schedule_wp_publish                                                 */
/* -------------------------------------------------------------------------- */

export const plannerScheduleWpPublish = defineCommand<
  z.infer<typeof scheduleWpPublishArgs>,
  WpPublishTarget
>({
  name: 'planner_schedule_wp_publish',
  section: 'calendar',
  consequence: 'writes-live-site',
  description:
    'Schedule an article to PUBLISH on the client live WordPress site at a given time. It will go live to the public at that time. To move it on the planner calendar without publishing, use planner_set_calendar_date instead.',
  args: scheduleWpPublishArgs,

  async resolve(input) {
    const article = await selectArticle(input);
    if (isRefusal(article)) return article;

    const publishAt = parseRequiredIsoDate(input.args.publishAt, 'publishAt');
    if (isRefusal(publishAt)) return publishAt;

    if (publishAt.getTime() <= Date.now()) {
      return refuse('publishAt is in the past, which would publish immediately rather than schedule.');
    }

    return { article, publishAt };
  },

  diff(target, tenant) {
    return articleDiff(
      target,
      tenant,
      'scheduled_publish_time',
      null,
      target.publishAt.toISOString(),
    );
  },

  async run(target: WpPublishTarget, auth: CommitAuthorization, write: WriteDeps): Promise<Receipt | Refusal> {
    if (!write.scheduleWpPublish) {
      return refuse('This surface cannot schedule WordPress publishing.');
    }
    return write.scheduleWpPublish(auth, target.article.id, target.publishAt);
  },
});
